import { Autocomplete, FormControl, TextField } from "@mui/material";
import { SyntheticEvent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useAddressAutocompleteQuery,
  useLazyGetAddressDetailsQuery,
} from "src/api/services/placesApi";
import { AutocompletePrediction, ShippingAddress } from "src/api/types";

interface Props<T> {
  onSelect: (callback: (a: T) => T) => void;
  value: string;
}

function PlacesAutocomplete<T extends ShippingAddress>({
  onSelect,
  value,
}: Props<T>) {
  const [getDetails] = useLazyGetAddressDetailsQuery();
  const { data: predictions = [] } = useAddressAutocompleteQuery(value);
  const { t } = useTranslation();
  const page = "shipping-information";

  const addressOptions = useMemo(
    () =>
      predictions.map((p) => ({
        description: p.description,
        place_id: p.place_id,
      })),
    [predictions]
  );

  const handleAddressChange = useCallback(
    (value: string) => {
      onSelect((prev) => ({
        ...prev,
        address_line1: value,
      }));
    },
    [onSelect]
  );

  const handleChange = useCallback(
    (
      _: SyntheticEvent<Element, Event>,
      newValue: string | AutocompletePrediction | null
    ) => {
      if (newValue != null && typeof newValue === "object") {
        getDetails(newValue.place_id)
          .unwrap()
          .then((data) => {
            onSelect((prev) => ({
              ...prev,
              ...data,
            }));
          });
      }
    },
    [getDetails, onSelect]
  );

  return (
    <FormControl error={value.length < 6} fullWidth required sx={{ gap: 1 }}>
      <Autocomplete
        inputValue={value}
        options={addressOptions}
        onChange={handleChange}
        disablePortal
        freeSolo
        disableClearable
        getOptionLabel={(o) => (typeof o === "string" ? o : o.description)}
        onInputChange={(_, value) => handleAddressChange(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            name="address_line1"
            id="address_line1"
            label={t(`${page}.address1.label`)}
          />
        )}
      />
    </FormControl>
  );
}

export default PlacesAutocomplete;
